import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeolocated } from 'react-geolocated';
import api from '../../api';
import PrefectureCitySelect from '../Common/PrefectureCitySelect';

// ── アイコン ───────────────────────────────────────────────────────
const StarIcon = ({ filled }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? '#fbbf24' : 'none'} stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);
const MapPinIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
    </svg>
);
const SearchIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const ChatIcon = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);
const NavIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="3 11 22 2 13 21 11 13 3 11" />
    </svg>
);
const ChevronIcon = ({ up }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: up ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const XIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

// ── 星評価 ─────────────────────────────────────────────────────────
const StarRating = ({ rating, reviewCount }) => {
    if (!rating) return null;
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {[...Array(full)].map((_, i) => <StarIcon key={`f${i}`} filled />)}
            {half && <span style={{ opacity: 0.5 }}><StarIcon filled /></span>}
            {[...Array(empty)].map((_, i) => (
                <svg key={`e${i}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
            ))}
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1a1a2e', marginLeft: 2 }}>{rating.toFixed(1)}</span>
            {reviewCount > 0 && (
                <span style={{ fontSize: '0.82rem', color: '#6b7280' }}>({reviewCount}件)</span>
            )}
        </div>
    );
};

// ── 検索タブ ───────────────────────────────────────────────────────
const TAB_BTN = (active) => ({
    flex: 1, padding: '12px 8px', background: 'none', border: 'none',
    cursor: 'pointer', position: 'relative',
    color: active ? '#005c53' : '#6b7280',
    fontWeight: active ? 600 : 400,
    fontSize: '0.875rem',
    transition: 'color 0.15s',
    borderBottom: active ? '2px solid #005c53' : '2px solid transparent',
});

const inputStyle = {
    width: '100%', padding: '10px 12px',
    border: '1px solid #e0e4ea', borderRadius: 4,
    fontSize: '0.875rem', color: '#1a1a2e',
    background: '#fff', outline: 'none',
    boxSizing: 'border-box',
};

// ── メイン ─────────────────────────────────────────────────────────
const SearchContractor = () => {
    const navigate = useNavigate();
    const customerId = localStorage.getItem('customer_id');

    const [searchMode, setSearchMode] = useState('area'); // 'location' | 'area' | 'name'
    const [contractors, setContractors] = useState([]);
    const [nameQuery, setNameQuery] = useState('');
    const [prefecture, setPrefecture] = useState('');
    const [city, setCity] = useState('');
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);
    const [selectedIds, setSelectedIds] = useState(
        () => JSON.parse(sessionStorage.getItem('selected_contractors') || '[]')
    );
    const [selectedContractors, setSelectedContractors] = useState([]);
    const [activeProjectMap, setActiveProjectMap] = useState({});
    const [panelOpen, setPanelOpen] = useState(false);

    useEffect(() => {
        if (!customerId) return;
        api.get(`projects/by_customer/${customerId}`)
            .then(r => {
                const map = {};
                r.data.filter(p => !['lost', 'completed'].includes(p.status))
                    .forEach(p => { map[p.contractor_id] = p.id; });
                setActiveProjectMap(map);
            })
            .catch(console.error);
    }, [customerId]);

    useEffect(() => {
        if (selectedIds.length === 0) { setSelectedContractors([]); return; }
        setSelectedContractors(prev => {
            const kept = prev.filter(c => selectedIds.includes(c.id));
            const keptIds = kept.map(c => c.id);
            const missing = selectedIds.filter(id => !keptIds.includes(id));
            if (missing.length > 0) {
                Promise.all(missing.map(id => api.get(`contractors/${id}`).then(r => r.data)))
                    .then(fetched => setSelectedContractors(prev2 => {
                        const kept2 = prev2.filter(c => selectedIds.includes(c.id));
                        const keptIds2 = kept2.map(c => c.id);
                        return [...kept2, ...fetched.filter(c => !keptIds2.includes(c.id))];
                    })).catch(console.error);
            }
            return kept;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIds]);

    const { coords, isGeolocationAvailable, isGeolocationEnabled } = useGeolocated({
        positionOptions: { enableHighAccuracy: true },
        userDecisionTimeout: 5000,
    });

    const fetchByLocation = async () => {
        if (!isGeolocationAvailable || !isGeolocationEnabled) { setError('位置情報が利用できません。'); return; }
        if (!coords) { setError('現在地を取得中です。少し待ってから再度お試しください。'); return; }
        setLoading(true); setError(null);
        try {
            const res = await api.get(`contractors/search?lat=${coords.latitude}&lng=${coords.longitude}`);
            setContractors(res.data); setSearched(true);
            if (res.data.length === 0) setError('近くに登録されている業者が見つかりませんでした。');
        } catch { setError('検索に失敗しました。'); }
        finally { setLoading(false); }
    };

    const fetchByAddress = async () => {
        if (!prefecture) { setError('都道府県を選択してください。'); return; }
        setLoading(true); setError(null);
        try {
            const params = new URLSearchParams({ prefecture });
            if (city) params.append('city', city);
            const res = await api.get(`contractors/search?${params}`);
            setContractors(res.data); setSearched(true);
            if (res.data.length === 0) setError('該当する業者が見つかりませんでした。');
        } catch { setError('検索に失敗しました。'); }
        finally { setLoading(false); }
    };

    const fetchByName = async () => {
        if (!nameQuery.trim()) { setError('業者名を入力してください。'); return; }
        setLoading(true); setError(null);
        try {
            const res = await api.get('contractors');
            const filtered = res.data.filter(c => c.company_name.includes(nameQuery.trim()));
            setContractors(filtered); setSearched(true);
            if (filtered.length === 0) setError('該当する業者が見つかりませんでした。');
        } catch { setError('検索に失敗しました。'); }
        finally { setLoading(false); }
    };

    const toggleSelect = (contractorId) => {
        setSelectedIds(prev => {
            const next = prev.includes(contractorId)
                ? prev.filter(id => id !== contractorId)
                : [...prev, contractorId];
            sessionStorage.setItem('selected_contractors', JSON.stringify(next));
            return next;
        });
    };

    const handleProceed = () => {
        if (selectedIds.length === 0) { setError('業者を1社以上選択してください。'); return; }
        navigate('/customer/condition');
    };

    const handleTabChange = (mode) => {
        setSearchMode(mode);
        setError(null);
    };

    return (
        <div style={{ minHeight: '80vh', background: '#f5f6f8', paddingBottom: selectedIds.length > 0 ? (panelOpen ? 370 : 88) : 40 }}>
            <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 16px' }}>

                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a1a2e', marginBottom: 28 }}>
                    業者を探す
                </h1>

                {/* 検索タブカード */}
                <div style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24 }}>
                    {/* タブ行 */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e0e4ea' }}>
                        <button style={TAB_BTN(searchMode === 'location')} onClick={() => handleTabChange('location')}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <NavIcon /> 現在地
                            </span>
                        </button>
                        <button style={TAB_BTN(searchMode === 'area')} onClick={() => handleTabChange('area')}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <MapPinIcon /> エリア
                            </span>
                        </button>
                        <button style={TAB_BTN(searchMode === 'name')} onClick={() => handleTabChange('name')}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                <SearchIcon /> 名前
                            </span>
                        </button>
                    </div>

                    {/* タブコンテンツ */}
                    <div style={{ padding: '20px 24px' }}>
                        {searchMode === 'location' && (
                            <div>
                                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: 16 }}>
                                    現在地の位置情報を使用して、近くの業者を検索します
                                </p>
                                <button
                                    onClick={fetchByLocation}
                                    disabled={loading}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
                                >
                                    <NavIcon /> 現在地から検索
                                </button>
                            </div>
                        )}
                        {searchMode === 'area' && (
                            <div>
                                <div style={{ marginBottom: 16 }}>
                                    <PrefectureCitySelect
                                        prefecture={prefecture} city={city}
                                        onPrefectureChange={setPrefecture} onCityChange={setCity}
                                        showLabel={true}
                                    />
                                </div>
                                <button
                                    onClick={fetchByAddress}
                                    disabled={loading || !prefecture}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '10px', background: prefecture ? '#005c53' : '#e0e4ea', color: prefecture ? '#fff' : '#aaa', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: '0.875rem', cursor: prefecture ? 'pointer' : 'not-allowed' }}
                                >
                                    <SearchIcon /> 検索
                                </button>
                            </div>
                        )}
                        {searchMode === 'name' && (
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                    <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#1a1a2e', marginBottom: 6 }}>業者名</label>
                                    <input
                                        type="text"
                                        placeholder="業者名を入力してください"
                                        value={nameQuery}
                                        onChange={e => setNameQuery(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && fetchByName()}
                                        style={inputStyle}
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                                    <button
                                        onClick={fetchByName}
                                        disabled={loading}
                                        style={{ padding: '10px 28px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                    >
                                        検索
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* エラー */}
                {error && (
                    <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: '0.85rem', color: '#795548' }}>
                        {error}
                    </div>
                )}

                {/* 検索結果 */}
                {searched && (
                    <>
                        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 12 }}>
                            検索結果 {contractors.length}件
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 24 }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>検索中...</div>
                            ) : contractors.map(c => {
                                const isSelected = selectedIds.includes(c.id);
                                const activeProjectId = activeProjectMap[c.id];
                                return (
                                    <div key={c.id} style={{
                                        background: activeProjectId ? '#fff8e1' : '#fff',
                                        borderRadius: 8,
                                        boxShadow: activeProjectId ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                                        border: activeProjectId ? '1px solid #fde68a' : 'none',
                                        padding: '20px 24px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 24,
                                        flexWrap: 'wrap',
                                    }}>
                                        {/* 左: 情報 */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            {/* 社名 + バッジ */}
                                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                                                    {c.company_name}
                                                </h3>
                                                {activeProjectId && (
                                                    <span style={{ padding: '3px 10px', background: '#fbbf24', color: '#78350f', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                                                        やり取り中
                                                    </span>
                                                )}
                                            </div>
                                            {/* 所在地 */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 }}>
                                                <MapPinIcon />
                                                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{c.prefecture} {c.city}</span>
                                            </div>
                                            {/* 評価 */}
                                            {c.performance?.rating && (
                                                <div style={{ marginBottom: 12 }}>
                                                    <StarRating
                                                        rating={c.performance.rating}
                                                        reviewCount={c.performance.review_count || c.performance.completed_count || 0}
                                                    />
                                                </div>
                                            )}
                                            {/* タグ */}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {c.qualification_certificate &&
                                                    c.qualification_certificate.split('\n').filter(Boolean).map((q, i) => (
                                                        <span key={i} style={{ padding: '4px 12px', background: '#e6f2f1', color: '#005c53', borderRadius: 20, fontSize: '0.8rem', fontWeight: 500 }}>
                                                            {q}
                                                        </span>
                                                    ))
                                                }
                                                {c.performance?.completed_count > 0 && (
                                                    <span style={{ padding: '4px 12px', background: '#f0f4ff', color: '#3b4dbf', borderRadius: 20, fontSize: '0.8rem', fontWeight: 500 }}>
                                                        実績{c.performance.completed_count}件
                                                    </span>
                                                )}
                                            </div>
                                            {/* 説明 */}
                                            {c.description && (
                                                <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: '10px 0 0', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                    {c.description}
                                                </p>
                                            )}
                                        </div>

                                        {/* 右: ボタン */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0, minWidth: 160, justifyContent: 'center' }}>
                                            {activeProjectId ? (
                                                <button
                                                    onClick={() => navigate(`/customer/chat/${activeProjectId}`)}
                                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px 20px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                                                >
                                                    <ChatIcon /> チャットへ
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => navigate(`/customer/contractor/${c.id}`)}
                                                        style={{ padding: '10px 20px', background: '#fff', color: '#005c53', border: '1px solid #005c53', borderRadius: 4, fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', textAlign: 'center' }}
                                                    >
                                                        詳細を見る
                                                    </button>
                                                    <button
                                                        onClick={() => toggleSelect(c.id)}
                                                        style={{ padding: '10px 20px', background: isSelected ? '#6b7280' : '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                                                    >
                                                        {isSelected ? '選択解除' : '選択する'}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* 選択業者パネル（展開時） */}
            {selectedIds.length > 0 && panelOpen && (
                <div style={{
                    position: 'fixed', bottom: 72, left: 0, right: 0,
                    background: '#fff', borderTop: '1px solid #e0e4ea',
                    boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
                    zIndex: 49, maxHeight: 280, overflowY: 'auto',
                }}>
                    {selectedContractors.map(c => (
                        <div key={c.id} style={{
                            display: 'flex', alignItems: 'center',
                            padding: '12px 24px', gap: 12,
                            borderBottom: '1px solid #f5f6f8',
                        }}>
                            <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {c.company_name}
                            </span>
                            <button
                                onClick={() => navigate(`/customer/contractor/${c.id}`)}
                                style={{ padding: '6px 14px', background: '#fff', color: '#005c53', border: '1px solid #005c53', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                                詳細
                            </button>
                            <button
                                onClick={() => toggleSelect(c.id)}
                                style={{ padding: '5px 8px', background: 'none', border: '1px solid #e0e4ea', borderRadius: 4, cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                            >
                                <XIcon />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* 固定フッターバー */}
            {selectedIds.length > 0 && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0,
                    background: '#fff', borderTop: '1px solid #e0e4ea',
                    boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
                    zIndex: 50, padding: '14px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <button
                        onClick={() => setPanelOpen(!panelOpen)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                    >
                        <div>
                            <div style={{ fontSize: '0.9rem', color: '#1a1a2e' }}>
                                <span style={{ fontWeight: 700 }}>{selectedIds.length}</span>
                                <span style={{ color: '#6b7280', marginLeft: 4 }}>件選択中</span>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#005c53', marginTop: 2 }}>
                                {panelOpen ? '閉じる' : '業者を確認する'}
                            </div>
                        </div>
                        <ChevronIcon up={panelOpen} />
                    </button>
                    <button
                        onClick={handleProceed}
                        style={{ padding: '11px 28px', background: '#005c53', color: '#fff', border: 'none', borderRadius: 4, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,92,83,0.3)', flexShrink: 0 }}
                    >
                        見積依頼へ進む →
                    </button>
                </div>
            )}
        </div>
    );
};

export default SearchContractor;
